import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Checkbox, DatePicker, Form, Input, InputNumber, Radio, Select, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { TextButton } from '../text_button/TextButton';
import BackButton from '../back_button/BackButton';
import ClearErrors from '../clear_errors/ClearErrors';
import ProjectTemplateDetailFields from '../project_template_detail_fields/ProjectTemplateDetailFields';
import ProjectInternalTeamsField, {
	getInternalTeamIdsFromValues,
	getInternalTeamsInitialValues,
} from '../project_internal_teams_field/ProjectInternalTeamsField';
import useMountEffect from '../../hooks/useMountEffect';
import {
	PROJECT_BUDGET_TYPES,
	PROJECT_ENTITY_TYPES,
	PROJECT_ENTITY_TYPE_OPTIONS,
} from '../../constants/ProjectEntities';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { debounce } from '../../utils/PerformanceUtils';
import {
	getProjectTemplateDetails,
	getTemplateDetailPrefill,
} from '../../utils/ProjectTemplateUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { PROJECTS_CRUD_ACTION_CREATORS } from '../../actions/projects_actions';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';

const PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE = 'PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE';

interface ProjectEditFormProps {
	history: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	isApprovalForm?: any;
	updating: boolean;
	formData: any;
	currentUser: any;
	projectTemplates: any;
	projectTemplatesFetching: boolean;
	updateProject: any;
	fetchProjectTemplate: any;
	fetchProjectTemplates: any;
}

const ProjectEditForm: React.FC<ProjectEditFormProps> = ({
	history,
	redirectForwardUrl,
	onSuccess,
	isApprovalForm,
	updating,
	formData,
	currentUser,
	projectTemplates,
	projectTemplatesFetching,
	updateProject,
	fetchProjectTemplate,
	fetchProjectTemplates,
}) => {
	const [form] = Form.useForm();
	const [projectTemplate, setProjectTemplate] = useState({});
	const [submitting, setSubmitting] = useState(false);
	// Id of the template currently being read — including the one fetched on mount.
	// Saving while it is in flight would send projectTemplateDetails: [] (built from an
	// empty template), wiping the project's saved field values.
	const [loadingTemplateId, setLoadingTemplateId] = useState(
		nullSafeGet('projectTemplateId', formData) || null
	);
	// A read that FAILED leaves the same mismatch as one in flight — a selected id with
	// no content — and saving through it would send projectTemplateDetails: [] and wipe
	// the project's stored field values. Stay blocked until retry or reselect.
	const [failedTemplateId, setFailedTemplateId] = useState(null);

	const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
	const projectsV2 = isProjectsV2Enabled();
	// Internal teams are the buyer's own private suppliers, so only a buyer can scope a
	// project to them — an internal tech editing a project cannot.
	const isSupplier = userType === ROLE_TYPES.SUPPLIER;
	const currencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	// Seed "has a budget?" from the saved project so budget-less projects don't open
	// with Yes selected (which would force a required Budget field on an unrelated edit).
	const hasBudgetDefault =
		nullSafeGet('budget', formData) != null || nullSafeGet('budgetType', formData) ? 'yes' : 'no';

	// antd v4 does not re-render the form on every field change the way the legacy
	// Form.create HOC did, so anything the layout branches on has to be watched.
	const hasBudget = Form.useWatch('hasBudget', form) || hasBudgetDefault;
	const budgetType =
		Form.useWatch('budgetType', form) ||
		nullSafeGet('budgetType', formData) ||
		PROJECT_BUDGET_TYPES.total;
	const entities =
		Form.useWatch('entities', form) ||
		nullSafeGetOrElse('entities', formData, [PROJECT_ENTITY_TYPES.workOrders]);
	const hasDeliverables = entities.includes(PROJECT_ENTITY_TYPES.deliverables);

	const loadTemplate = (projectTemplateId) => {
		setLoadingTemplateId(projectTemplateId);
		setFailedTemplateId(null);
		return (
			fetchProjectTemplate(userType, projectTemplateId)
				.then((template) => {
					// Not cancellable: switching templates quickly can let an earlier response land
					// last, pairing the selected id with the previous template's fields. Drop any
					// response that is no longer the selection.
					if (`${form.getFieldValue('projectTemplateId')}` !== `${projectTemplateId}`) {
						return;
					}
					setProjectTemplate(template);
					// The template's fields mount only now, so their saved values can't have come
					// through initialValues.
					const prefill = getTemplateDetailPrefill(
						template,
						nullSafeGetOrElse('projectTemplateDetails', formData, [])
					);
					if (Object.keys(prefill).length > 0) {
						form.setFieldsValue(prefill);
					}
				})
				.catch(() => {
					if (`${form.getFieldValue('projectTemplateId')}` === `${projectTemplateId}`) {
						setFailedTemplateId(projectTemplateId);
					}
				})
				// Only the newest read clears the flag, so a superseded one resolving late
				// cannot re-enable the button while the current template is still loading.
				.finally(() =>
					setLoadingTemplateId((current) =>
						`${current}` === `${projectTemplateId}` ? null : current
					)
				)
		);
	};

	const retryTemplate = () => loadTemplate(form.getFieldValue('projectTemplateId'));

	useMountEffect(() => {
		fetchProjectTemplates(userType, { limit: 50, offset: 0 });
		const projectTemplateId = nullSafeGet('projectTemplateId', formData);
		if (projectTemplateId) {
			loadTemplate(projectTemplateId);
		} else {
			setLoadingTemplateId(null);
		}
	});

	const debouncedSearchProjectTemplates = React.useRef(
		debounce(
			(searchText) =>
				fetchProjectTemplates(nullSafeGetOrElse('userType', currentUser, 'buyer'), {
					name: searchText && searchText.trim(),
				}),
			500,
			false
		)
	);

	const handleProjectTemplateChange = (projectTemplateId) => {
		if (!projectTemplateId) {
			setLoadingTemplateId(null);
			setFailedTemplateId(null);
			setProjectTemplate({});
			return;
		}
		loadTemplate(projectTemplateId);
	};

	const handleFinish = (values) => {
		const budgeted = values.hasBudget === 'yes';
		const isTotalBudget = budgeted && values.budgetType === PROJECT_BUDGET_TYPES.total;
		const internalTeamIds = getInternalTeamIdsFromValues(values);
		const project = {
			...formData,
			title: values.title,
			description: values.additionalInfo,
			// projects-v2 adds start/end dates, budget type, and entities; the legacy form
			// only had a due date + budget.
			...(projectsV2
				? {
						startDate: values.startDate,
						endDate: values.endDate,
						dueDate: values.endDate,
						// Send explicit null (not undefined) when clearing — undefined is dropped
						// by JSON.stringify, so the backend would keep the old budget.
						budgetType: budgeted ? values.budgetType : null,
						budget: isTotalBudget ? values.budget : null,
						entities: values.entities,
						// Buyers only — the field isn't rendered for an internal tech, so sending a
						// value here would wipe the buyer's scoping on any supplier-side edit. For a
						// buyer it is always sent, empty included: an empty list is meaningful (every
						// internal team can see the project) and is how the last team gets removed,
						// where undefined would be dropped and the old list kept.
						...(isSupplier
							? {}
							: { supplierFacilityIds: internalTeamIds === null ? [] : internalTeamIds }),
				  }
				: {
						dueDate: values.dueDate,
						budget: values.budget,
				  }),
			buyerCompanyId:
				nullSafeGet('buyerCompanyId', formData) ||
				nullSafeGet('facility.buyerCompanyId', currentUser),
			createdBy: nullSafeGet('createdBy', formData) || nullSafeGet('email', currentUser),
			// Explicit null when the template is cleared: undefined is dropped by
			// JSON.stringify, so the project would keep its old template id while its
			// field values (projectTemplateDetails below) are saved as empty. The API's
			// optValue reads JSON null as None (DataValidator.optValue).
			projectTemplateId: values.projectTemplateId ? parseInt(values.projectTemplateId, 10) : null,
			projectTemplateDetails: getProjectTemplateDetails(projectTemplate, values),
		};
		setSubmitting(true);
		updateProject(userType, project)
			.then((record) => {
				if (redirectForwardUrl) {
					history.push(redirectForwardUrl);
				}
				if (onSuccess) {
					onSuccess(record);
				}
			})
			.finally(() => setSubmitting(false));
	};

	const projectTemplatesDropdownRecords = getRecordsForTargetCollection(
		projectTemplates,
		PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
	);
	const entityOptions = PROJECT_ENTITY_TYPE_OPTIONS.map((option) =>
		// Every project has work orders — that entity is locked on.
		option.value === PROJECT_ENTITY_TYPES.workOrders ? { ...option, disabled: true } : option
	);
	const savedDueDate = nullSafeGet('dueDate', formData);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleFinish}
			className="supplierForm"
			initialValues={{
				title: nullSafeGet('title', formData),
				additionalInfo: nullSafeGet('description', formData),
				// Fall back to the legacy dueDate so a legacy project (which only has dueDate)
				// can be saved without forcing a brand-new start date.
				startDate: nullSafeGet('startDate', formData)
					? moment(formData.startDate)
					: savedDueDate
					? moment(savedDueDate)
					: undefined,
				endDate: nullSafeGet('endDate', formData)
					? moment(formData.endDate)
					: savedDueDate
					? moment(savedDueDate)
					: undefined,
				// Legacy path keeps the time-of-day picker so editing doesn't rewrite the due
				// date to 00:00.
				dueDate: savedDueDate ? moment(savedDueDate) : undefined,
				hasBudget: hasBudgetDefault,
				budgetType: nullSafeGet('budgetType', formData) || PROJECT_BUDGET_TYPES.total,
				budget: nullSafeGet('budget', formData),
				projectTemplateId: nullSafeGet('projectTemplateId', formData),
				entities: nullSafeGetOrElse('entities', formData, [PROJECT_ENTITY_TYPES.workOrders]),
				...getInternalTeamsInitialValues(formData),
			}}
		>
			<ClearErrors actionCreators={PROJECTS_CRUD_ACTION_CREATORS} />
			<Form.Item
				label="Title"
				name="title"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<Input style={{ maxWidth: 640 }} />
			</Form.Item>
			<Form.Item label="Scope" name="additionalInfo">
				<Input.TextArea
					style={{ maxWidth: 640 }}
					autoSize={{ minRows: 3 }}
					placeholder="Describe the project's scope — what's included, goals, or any context"
				/>
			</Form.Item>
			{projectsV2 ? (
				<div style={{ display: 'flex', gap: 16, maxWidth: 640 }}>
					<Form.Item
						label="Start Date"
						name="startDate"
						style={{ flex: 1 }}
						rules={[{ required: true, message: 'This field is required.' }]}
					>
						<DatePicker style={{ width: '100%' }} format="MMM D, YYYY" />
					</Form.Item>
					<Form.Item label="End Date (optional)" name="endDate" style={{ flex: 1 }}>
						<DatePicker style={{ width: '100%' }} format="MMM D, YYYY" />
					</Form.Item>
				</div>
			) : (
				<Form.Item
					label="Due date"
					name="dueDate"
					rules={[{ required: true, message: 'This field is required.' }]}
				>
					<DatePicker
						style={{ width: '100%', maxWidth: 640 }}
						format="MMM D, YYYY h:mm A"
						showTime
					/>
				</Form.Item>
			)}
			{projectsV2 ? (
				<>
					<Form.Item label="Does this project have a budget?" name="hasBudget">
						<Radio.Group>
							<Radio value="yes">Yes</Radio>
							<Radio value="no">No</Radio>
						</Radio.Group>
					</Form.Item>
					{hasBudget === 'yes' ? (
						<Form.Item name="budgetType">
							<Radio.Group>
								<div className="flex flex-col gap-3">
									<div>
										<Radio value={PROJECT_BUDGET_TYPES.total}>Total Budget</Radio>
										<div
											className="footnoteregular text-character-secondary"
											style={{ marginLeft: 24 }}
										>
											Budget allocated for the entire project.
										</div>
									</div>
									<div>
										<Radio value={PROJECT_BUDGET_TYPES.individual} disabled={!hasDeliverables}>
											Individual Budget
										</Radio>
										<div
											className="footnoteregular text-character-secondary"
											style={{ marginLeft: 24 }}
										>
											Each deliverable created will have its own budget
											{hasDeliverables ? '' : ' — enable Deliverables to use this'}
										</div>
									</div>
								</div>
							</Radio.Group>
						</Form.Item>
					) : null}
					{hasBudget === 'yes' && budgetType === PROJECT_BUDGET_TYPES.total ? (
						<Form.Item
							label="Budget"
							name="budget"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<InputNumber
								min={0}
								style={{ display: 'flex', width: '100%', maxWidth: 640 }}
								addonAfter={currencyId}
							/>
						</Form.Item>
					) : null}
				</>
			) : (
				<Form.Item
					label="Budget"
					name="budget"
					rules={[{ required: true, message: 'This field is required.' }]}
				>
					<InputNumber
						min={0}
						style={{ display: 'flex', width: '100%', maxWidth: 640 }}
						addonAfter={currencyId}
					/>
				</Form.Item>
			)}
			<Form.Item label="Project Template" name="projectTemplateId">
				<Select
					notFoundContent={projectTemplatesFetching ? <Spin size="small" /> : null}
					placeholder="Type to search for project template"
					filterOption={false}
					showSearch={true}
					allowClear
					onSearch={(searchText) => debouncedSearchProjectTemplates.current(searchText)}
					onChange={handleProjectTemplateChange}
					style={{ maxWidth: 640 }}
				>
					{projectTemplatesDropdownRecords.map((template) => (
						<Select.Option value={template.id} key={template.id}>
							{template.name}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			{failedTemplateId ? (
				<div className="footnoteregular mb-4 flex items-start gap-2" style={{ maxWidth: 640 }}>
					<ExclamationCircleOutlined className="text-[12px] !leading-[20px] text-sunsetOrange-8" />
					<span className="text-sunsetOrange-8">
						{"Couldn't load this template, so its fields can't be shown or saved. "}
						<TextButton onClick={retryTemplate}>Retry</TextButton>
					</span>
				</div>
			) : null}
			<ProjectTemplateDetailFields projectTemplate={projectTemplate} />
			{projectsV2 && !isSupplier ? <ProjectInternalTeamsField form={form} /> : null}
			{projectsV2 ? (
				<Form.Item label="Project Entities" required className="mb-4">
					<Form.Item
						name="entities"
						noStyle
						rules={[{ required: true, message: 'Select at least one entity.' }]}
					>
						<Checkbox.Group
							options={entityOptions}
							onChange={(checked) => {
								// Individual budget requires Deliverables — if it's removed, snap the budget
								// type back to Total so we never save individual-with-no-deliverables.
								if (
									!checked.includes(PROJECT_ENTITY_TYPES.deliverables) &&
									form.getFieldValue('budgetType') === PROJECT_BUDGET_TYPES.individual
								) {
									form.setFieldsValue({ budgetType: PROJECT_BUDGET_TYPES.total });
								}
							}}
						/>
					</Form.Item>
				</Form.Item>
			) : null}
			<Form.Item>
				<OWButton
					type="primary"
					htmlType="submit"
					// Also disabled while a template read is in flight — see loadingTemplateId.
					loading={updating || submitting || !!loadingTemplateId}
					// A failed template read leaves a selected id with no content, and saving
					// through it would clear the project's stored field values.
					disabled={!!failedTemplateId}
					className="contactForm__button"
				>
					{isApprovalForm ? 'Approve project' : 'Update project'}
				</OWButton>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,
	isApprovalForm: ownProps.isApprovalForm,
	formData: ownProps.formData,
	projectTemplatesFetching: state.project_templates.fetching,
	projectTemplates: state.project_templates,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	updateProject: (userType, project) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.update(project)
				: projectsRestCrudThunksForBuyer.update(project)
		),
	fetchProjectTemplate: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.readOne(id)
				: projectTemplatesRestCrudThunksForBuyer.readOne(id)
		),
	fetchProjectTemplates: (userType, params) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
				: projectTemplatesRestCrudThunksForBuyer.read(
						params,
						PROJECT_FORM_PROJECT_TEMPLATES_AUTOCOMPLETE
				  )
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectEditForm as any));
